import { isSupabaseEnabled, supabase } from './supabaseClient';
import { getActorRole } from '../utils/adminRoles';

const STORAGE_KEY = 'psag-activity-log';
const EMAIL_KEY = 'psag-admin-email';
const NAME_KEY = 'psag-admin-display-name';
const MAX_ENTRIES = 250;

export const CHURCH_MEMBER_NAMES = {
  'abel@psagchurch.org': 'Abel',
  'titus@psagchurch.org': 'Titus',
  'shawn@psagchurch.org': 'Shawn',
  'admin@psagchurch.org': 'Admin',
};

const listeners = new Set();

function getCurrentActorEmail() {
  try {
    return sessionStorage.getItem(EMAIL_KEY) || null;
  } catch {
    return null;
  }
}

export function getCurrentActorName() {
  try {
    const stored = sessionStorage.getItem(NAME_KEY);
    if (stored?.trim()) return stored.trim();
  } catch {
    // ignore
  }
  return resolveActorNameFromEmail(getCurrentActorEmail());
}

export function setCurrentActorName(name) {
  try {
    if (name?.trim()) {
      sessionStorage.setItem(NAME_KEY, name.trim());
    } else {
      sessionStorage.removeItem(NAME_KEY);
    }
  } catch {
    // ignore
  }
}

export function resolveActorNameFromEmail(email, preferredName) {
  if (preferredName?.trim()) return preferredName.trim();

  const normalized = email?.trim().toLowerCase();
  if (!normalized) return 'Unknown';

  if (CHURCH_MEMBER_NAMES[normalized]) {
    return CHURCH_MEMBER_NAMES[normalized];
  }

  const localPart = normalized.split('@')[0];
  return localPart.charAt(0).toUpperCase() + localPart.slice(1);
}

function cloneSnapshot(value) {
  if (value === undefined) return undefined;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

function toRow(entry) {
  return {
    id: entry.id,
    timestamp: entry.timestamp,
    type: entry.type,
    page: entry.page || null,
    content_key: entry.contentKey || null,
    label: entry.label,
    summary: entry.summary,
    location: entry.location || null,
    old_value: entry.oldValue === undefined ? null : entry.oldValue,
    new_value: entry.newValue === undefined ? null : entry.newValue,
    actor_email: entry.actorEmail || null,
    actor_name: entry.actorName || null,
    login_role: entry.loginRole || null,
    reverted: Boolean(entry.reverted),
    reverted_at: entry.revertedAt || null,
  };
}

function fromRow(row) {
  return {
    id: row.id,
    timestamp: row.timestamp,
    type: row.type,
    page: row.page,
    contentKey: row.content_key,
    label: row.label,
    summary: row.summary,
    location: row.location,
    oldValue: row.old_value === null ? undefined : row.old_value,
    newValue: row.new_value === null ? undefined : row.new_value,
    actorEmail: row.actor_email,
    actorName: row.actor_name,
    loginRole: row.login_role,
    reverted: Boolean(row.reverted),
    revertedAt: row.reverted_at,
  };
}

const CONTENT_LABELS = {
  'home.hero.welcome': 'Home — Welcome line',
  'home.hero.name_main': 'Home — Church name',
  'home.hero.name_secondary': 'Home — Church subtitle',
  'home.hero.description': 'Home — Hero description',
  'home.hero.slides': 'Home — Hero slideshow',
  'home.about.title': 'Home — About title',
  'home.about.p1': 'Home — About paragraph 1',
  'home.about.p2': 'Home — About paragraph 2',
  'home.about.image': 'Home — About image',
  'home.pastor.title': 'Home — Pastor section title',
  'home.pastor.p1': 'Home — Pastor bio 1',
  'home.pastor.p2': 'Home — Pastor bio 2',
  'home.pastor.image': 'Home — Pastor photo',
  'home.pastor.background': 'Home — Pastor section background',
  'home.pastor.name': 'Home — Pastor name',
  'home.sermons.title': 'Home — Sermons title',
  'home.sermons.items': 'Home — Sermon videos',
  'home.sermons.moreLink': 'Home — Sermons view more link',
  'home.verses.items': 'Home — Bible verses',
  'home.join.title': 'Home — Join section title',
  'home.join.description': 'Home — Join section description',
  'home.services.title': 'Home — Service times title',
  'home.services.items': 'Home — Service times',
  'home.convention.title': 'Home — Convention section title',
  'home.convention.flyer': 'Home — Convention flyer',
  'home.convention.theme_label': 'Home — Convention theme label',
  'home.convention.theme': 'Home — Convention theme',
  'home.convention.speaker': 'Home — Convention guest speaker',
  'home.convention.location': 'Home — Convention location',
  'calendar.customEvents': 'Calendar — Custom events',
  'gallery.hero.title': 'Gallery — Page title',
  'gallery.description': 'Gallery — Description',
  'gallery.images': 'Gallery — Photos',
  'story.hero.title': 'Story — Page title',
  'story.heading': 'Story — Section heading',
  'story.pastor_image': 'Story — Pastor photo',
  'story.image_title': 'Story — Image caption',
  'story.mission.title': 'Story — Mission title',
  'story.mission.description': 'Story — Mission description',
  'visit.hero.title': 'Visit — Page title',
  'visit.welcome': 'Visit — Welcome message',
  'beliefs.hero.title': 'Beliefs — Page title',
  'beliefs.intro': 'Beliefs — Introduction',
  'beliefs.section_title': 'Beliefs — Section title',
  'beliefs.items': 'Beliefs — Core beliefs',
  'beliefs.values.title': 'Beliefs — Our Values title',
  'beliefs.values.items': 'Beliefs — Our Values',
  'ministries.hero.title': 'Ministries — Page title',
  'ministries.description': 'Ministries — Description',
  'ministries.description_sub': 'Ministries — Sub-description',
  'watch.hero.title': 'Watch — Page title',
};

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

export function subscribeActivityLog(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function loadActivityLog() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistLog(entries) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  notifyListeners();
  return entries;
}

export async function fetchActivityLog() {
  if (!isSupabaseEnabled()) {
    return loadActivityLog();
  }

  try {
    const { data, error } = await supabase
      .from('activity_logs')
      .select('*')
      .order('timestamp', { ascending: false })
      .limit(MAX_ENTRIES);

    if (error) throw error;

    const entries = (data || []).map(fromRow);
    persistLog(entries);
    return entries;
  } catch (error) {
    console.error('Failed to fetch activity log from Supabase:', error);
    return loadActivityLog();
  }
}

function saveActivityToSupabase(entry) {
  if (!isSupabaseEnabled()) return;

  supabase
    .from('activity_logs')
    .insert(toRow(entry))
    .then(({ error }) => {
      if (error) console.error('Failed to save activity to Supabase:', error);
    });
}

export function appendActivity(entry) {
  const entries = loadActivityLog();
  const actorEmail = entry.actorEmail || getCurrentActorEmail();
  const actorName =
    entry.actorName ||
    resolveActorNameFromEmail(actorEmail, getCurrentActorName());

  const nextEntry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    timestamp: new Date().toISOString(),
    ...entry,
    actorEmail,
    actorName,
    loginRole: entry.loginRole || getActorRole(actorEmail),
    oldValue: cloneSnapshot(entry.oldValue),
    newValue: cloneSnapshot(entry.newValue),
    reverted: false,
    revertedAt: null,
  };
  const nextEntries = [nextEntry, ...entries].slice(0, MAX_ENTRIES);
  persistLog(nextEntries);
  saveActivityToSupabase(nextEntry);
  return nextEntries;
}

export async function deleteActivityEntry(id) {
  const entries = loadActivityLog().filter((entry) => entry.id !== id);
  persistLog(entries);

  if (!isSupabaseEnabled()) return { ok: true };

  try {
    const { error } = await supabase.from('activity_logs').delete().eq('id', id);
    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('Failed to delete activity log entry:', error);
    return { ok: false, error: 'Could not remove this entry.' };
  }
}

export async function markActivityReverted(id) {
  const entries = loadActivityLog().map((entry) =>
    entry.id === id
      ? { ...entry, reverted: true, revertedAt: new Date().toISOString() }
      : entry
  );
  persistLog(entries);

  if (!isSupabaseEnabled()) return { ok: true };

  try {
    const { error } = await supabase
      .from('activity_logs')
      .update({ reverted: true, reverted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    return { ok: true };
  } catch (error) {
    console.error('Failed to mark activity entry reverted:', error);
    return { ok: false, error: 'Could not update this entry.' };
  }
}

export async function clearActivityLog() {
  localStorage.removeItem(STORAGE_KEY);
  notifyListeners();

  if (!isSupabaseEnabled()) return;

  try {
    const { error } = await supabase
      .from('activity_logs')
      .delete()
      .neq('id', '');

    if (error) throw error;
  } catch (error) {
    console.error('Failed to clear activity log in Supabase:', error);
  }
}

function stripHtml(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function truncate(value, max = 90) {
  if (!value) return '';
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

function isImageValue(value) {
  return typeof value === 'string' && (value.startsWith('data:image') || /^https?:\/\//i.test(value));
}

function isImageKey(key) {
  return /(image|slides|thumbnail|background|gallery\.images|pastor_image|\.cover|\.images)/i.test(key);
}

export function formatContentLabel(key) {
  if (CONTENT_LABELS[key]) return CONTENT_LABELS[key];

  if (key.startsWith('home.marquee.')) return `Home — Marquee item ${Number(key.split('.').pop()) + 1}`;
  if (key.startsWith('home.verse.')) return `Home — Bible verse ${key.split('.')[2]}`;
  if (key.startsWith('home.services.')) return `Home — Service time (${key.replace('home.services.', '')})`;
  if (key.startsWith('home.sermons.') && key.endsWith('.thumbnail')) {
    return `Home — Sermon thumbnail ${Number(key.split('.')[2]) + 1}`;
  }
  if (key.startsWith('beliefs.')) return `Beliefs — ${key.replace('beliefs.', '').replace(/_/g, ' ')}`;
  if (key.startsWith('ministries.')) return `Ministries — ${key.replace('ministries.', '').replace(/_/g, ' ')}`;
  if (key.startsWith('watch.section.')) return `Watch — ${key.replace('watch.section.', 'Section ').replace(/_/g, ' ')}`;
  if (key.startsWith('watch.')) return `Watch — ${key.replace('watch.', '').replace(/_/g, ' ')}`;
  if (key.startsWith('story.')) return `Story — ${key.replace('story.', '').replace(/_/g, ' ')}`;

  return key
    .split('.')
    .map((part) => part.replace(/_/g, ' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' — ');
}

export function summarizeContentChange(key, oldValue, newValue) {
  if (key === 'calendar.customEvents' && Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : 0;
    if (newValue.length > oldLength) {
      const added = newValue[newValue.length - 1];
      return `Added calendar event "${added?.title || 'New event'}"`;
    }
    if (newValue.length < oldLength) {
      return `Removed calendar event (${oldLength} → ${newValue.length})`;
    }
    return 'Updated calendar event';
  }

  if (key === 'home.services.items' && Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : 0;
    if (newValue.length > oldLength) return 'Added service day';
    if (newValue.length < oldLength) return 'Removed service day';
    return 'Updated service times';
  }

  if (key === 'home.sermons.items' && Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : 0;
    if (newValue.length > oldLength) return 'Added sermon video';
    if (newValue.length < oldLength) return 'Removed sermon video';
    return 'Updated sermon videos';
  }

  if (key === 'home.verses.items' && Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : 0;
    if (newValue.length > oldLength) return 'Added Bible verse';
    if (newValue.length < oldLength) return 'Removed Bible verse';
    return 'Updated Bible verses';
  }

  if (/^watch\.section\.\d+\.videos$/.test(key) && Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : 0;
    if (newValue.length > oldLength) return 'Added watch video';
    if (newValue.length < oldLength) return 'Removed watch video';
    return 'Updated watch videos';
  }

  if (key === 'beliefs.items' && Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : 0;
    if (newValue.length > oldLength) return 'Added core belief';
    if (newValue.length < oldLength) return 'Removed core belief';
    return 'Updated core beliefs';
  }

  if (key === 'beliefs.values.items' && Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : 0;
    if (newValue.length > oldLength) return 'Added belief value';
    if (newValue.length < oldLength) return 'Removed belief value';
    return 'Updated Our Values section';
  }

  if (newValue === undefined || newValue === null) {
    if (Array.isArray(oldValue)) return `Gallery reset to defaults (${oldValue.length} custom images cleared)`;
    if (isImageValue(oldValue) || isImageKey(key)) return 'Image reset to default';
    if (oldValue !== undefined) return 'Text reset to default';
    return 'Removed custom value';
  }

  if (Array.isArray(newValue)) {
    const oldLength = Array.isArray(oldValue) ? oldValue.length : null;
    if (oldLength === null) return `Set gallery to ${newValue.length} image${newValue.length === 1 ? '' : 's'}`;
    if (newValue.length > oldLength) {
      return `Added image (${oldLength} → ${newValue.length} total)`;
    }
    if (newValue.length < oldLength) {
      return `Removed image (${oldLength} → ${newValue.length} total)`;
    }
    return `Updated gallery image (${newValue.length} total)`;
  }

  if (isImageValue(newValue) || isImageKey(key)) {
    if (oldValue === undefined) return 'Image updated';
    return 'Image replaced';
  }

  const nextText = truncate(stripHtml(String(newValue)));
  const oldText = oldValue === undefined ? '' : truncate(stripHtml(String(oldValue)));

  if (!oldText) return `Set to "${nextText}"`;
  return `Changed from "${oldText}" to "${nextText}"`;
}

export function getChangeType(key, oldValue, newValue) {
  if (key === 'calendar.customEvents') return 'calendar';
  if (key === 'home.services.items') return 'service_times';
  if (Array.isArray(newValue) || Array.isArray(oldValue)) return 'gallery';
  if (isImageValue(newValue) || isImageValue(oldValue) || isImageKey(key)) return 'image';
  return 'text';
}

export function logContentChange({ key, oldValue, newValue, page, actorEmail, actorName }) {
  if (oldValue === newValue) return;

  const changeType = getChangeType(key, oldValue, newValue);
  const isReset = newValue === undefined || (oldValue !== undefined && newValue === undefined);
  const editorName = actorName || getCurrentActorName();
  const baseSummary = summarizeContentChange(key, oldValue, newValue);
  const summary =
    editorName && editorName !== 'Unknown'
      ? `${editorName} — ${baseSummary}`
      : baseSummary;

  appendActivity({
    type: isReset ? 'reset' : changeType,
    page: page || window.location.pathname,
    contentKey: key,
    label: formatContentLabel(key),
    summary,
    oldValue: cloneSnapshot(oldValue),
    newValue: cloneSnapshot(newValue),
    actorEmail,
    actorName: editorName,
  });
}

const ADMIN_EMAIL = 'admin@psagchurch.org';

function getLoginLabel(email) {
  return getActorRole(email) === 'admin' ? 'Admin sign in' : 'Church member sign in';
}

function getLoginSummary(email, displayName) {
  const normalized = email.trim().toLowerCase();
  const name = displayName?.trim() || CHURCH_MEMBER_NAMES[normalized];
  if (name) return `${name} signed in`;
  if (normalized === ADMIN_EMAIL) return `Signed in as ${email}`;
  return `Church member signed in (${email})`;
}

export function logLogin({ email, page, location, displayName }) {
  const normalized = email.trim().toLowerCase();
  const actorName = resolveActorNameFromEmail(normalized, displayName);

  appendActivity({
    type: 'login',
    loginRole: getActorRole(email),
    page: page || window.location.pathname,
    label: getLoginLabel(email),
    summary: getLoginSummary(email, displayName),
    location,
    actorEmail: normalized,
    actorName,
  });
}

export function logAdminRevert({ contentKey, label, summary, page }) {
  const actorName = getCurrentActorName();
  const baseSummary = summary || 'Admin reverted a previous change';

  appendActivity({
    type: 'reset',
    page: page || window.location.pathname,
    contentKey,
    label: label || formatContentLabel(contentKey),
    summary: actorName && actorName !== 'Unknown' ? `${actorName} — ${baseSummary}` : baseSummary,
    loginRole: 'admin',
    actorName,
  });
}

export function logResetAll({ page }) {
  const actorName = getCurrentActorName();

  appendActivity({
    type: 'reset_all',
    page: page || window.location.pathname,
    label: 'Site content',
    summary:
      actorName && actorName !== 'Unknown'
        ? `${actorName} — Reset all edited content to defaults`
        : 'Reset all edited content to defaults',
    actorName,
  });
}

export async function fetchLoginLocation() {
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) throw new Error('Location lookup failed');
    const data = await response.json();
    if (data.error) throw new Error(data.reason || 'Location lookup failed');

    return {
      city: data.city || null,
      region: data.region || null,
      country: data.country_name || null,
      ip: data.ip || null,
      timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  } catch {
    return {
      city: null,
      region: null,
      country: null,
      ip: null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }
}

export function formatLocation(location) {
  if (!location) return 'Location unavailable';

  const parts = [location.city, location.region, location.country].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(', ');
  }

  if (location.timezone) return `Timezone: ${location.timezone}`;
  return 'Location unavailable';
}

export function formatActivityType(type, entry) {
  if (type === 'login') {
    if (entry?.loginRole === 'admin' || entry?.label === 'Admin sign in') {
      return 'Admin sign in';
    }
    return 'Church member';
  }

  const labels = {
    text: 'Text edit',
    image: 'Image edit',
    calendar: 'Calendar edit',
    service_times: 'Service times',
    gallery: 'Gallery edit',
    reset_all: 'Reset all',
  };
  return labels[type] || type;
}

export function formatTimestamp(isoString) {
  return new Date(isoString).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
